import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { Payment, PaymentCreateDto } from "../../modules/shared/model";
import { createPayment as createPaymentAction, updatePayment as updatePaymentAction, getPaymentById as getPaymentByIdAction, getPayments as getPaymentsAction, deletePayment as deletePaymentAction, getPaymentsByMilestones as getPaymentsByMilestonesAction } from "../Actions/PaymentActions";

export const createPayment = createAsyncThunk("payments/create", async (data: PaymentCreateDto, { rejectWithValue }) => {
	try { return await createPaymentAction(data); } catch (e: any) { return rejectWithValue(e?.message || "Failed to create payment"); }
});

export const updatePayment = createAsyncThunk("payments/update", async ({ id, data }: { id: string; data: Partial<PaymentCreateDto> }, { rejectWithValue }) => {
	try { return await updatePaymentAction(id, data); } catch (e: any) { return rejectWithValue(e?.message || "Failed to update payment"); }
});

export const fetchPayment = createAsyncThunk("payments/getById", async (id: string, { rejectWithValue }) => {
	try { return await getPaymentByIdAction(id); } catch (e: any) { return rejectWithValue(e?.message || "Failed to fetch payment"); }
});

export const fetchPayments = createAsyncThunk("payments/get", async (query: Record<string, string | number | boolean> | undefined, { rejectWithValue }) => {
	try { return await getPaymentsAction(query); } catch (e: any) { return rejectWithValue(e?.message || "Failed to fetch payments"); }
});

export const deletePayment = createAsyncThunk("payments/delete", async (id: string, { rejectWithValue }) => {
	try { await deletePaymentAction(id); return id; } catch (e: any) { return rejectWithValue(e?.message || "Failed to delete payment"); }
});

export const fetchPaymentsByMilestones = createAsyncThunk("payments/getByMilestones", async (milestoneIds: string[], { rejectWithValue }) => {
	try { return await getPaymentsByMilestonesAction(milestoneIds); } catch (e: any) { return rejectWithValue(e?.message || "Failed to fetch payments by milestones"); }
});

interface PaymentsState {
	items: Payment[];
	current: Payment | null;
	loading: boolean;
	error: string | null;
	totalCount?: number;
}

const initialState: PaymentsState = {
	items: [],
	current: null,
	loading: false,
	error: null,
	totalCount: 0,
};

const paymentsSlice = createSlice({
	name: "payments",
	initialState,
	reducers: {
		clearPaymentsError(state) { state.error = null; },
		clearCurrentPayment(state) { state.current = null; },
	},
	extraReducers: (builder) => {
		builder
			.addCase(createPayment.pending, (s) => { s.loading = true; s.error = null; })
			.addCase(createPayment.fulfilled, (s, a: PayloadAction<Payment>) => { s.loading = false; s.items.unshift(a.payload); s.totalCount = (s.totalCount || 0) + 1; })
			.addCase(createPayment.rejected, (s, a: any) => { s.loading = false; s.error = a.payload as string; });

		builder
			.addCase(updatePayment.pending, (s) => { s.loading = true; s.error = null; })
			.addCase(updatePayment.fulfilled, (s, a: PayloadAction<Payment>) => {
				s.loading = false;
				const i = s.items.findIndex(x => x.id === a.payload.id);
				if (i !== -1) s.items[i] = a.payload;
				if (s.current?.id === a.payload.id) s.current = a.payload;
			})
			.addCase(updatePayment.rejected, (s, a: any) => { s.loading = false; s.error = a.payload as string; });

		builder
			.addCase(fetchPayment.pending, (s) => { s.loading = true; s.error = null; })
			.addCase(fetchPayment.fulfilled, (s, a: PayloadAction<Payment>) => { s.loading = false; s.current = a.payload; })
			.addCase(fetchPayment.rejected, (s, a: any) => { s.loading = false; s.error = a.payload as string; });

		builder
			.addCase(fetchPayments.pending, (s) => { s.loading = true; s.error = null; })
			.addCase(fetchPayments.fulfilled, (s, a: PayloadAction<{ data: Payment[]; totalCount?: number }>) => { s.loading = false; s.items = a.payload.data || []; s.totalCount = a.payload.totalCount || 0; })
			.addCase(fetchPayments.rejected, (s, a: any) => { s.loading = false; s.error = a.payload as string; });

		builder
			.addCase(deletePayment.pending, (s) => { s.loading = true; s.error = null; })
			.addCase(deletePayment.fulfilled, (s, a: PayloadAction<string>) => { s.loading = false; s.items = s.items.filter(x => x.id !== a.payload); if (s.current?.id === a.payload) s.current = null; s.totalCount = Math.max(0, (s.totalCount || 0) - 1); })
			.addCase(deletePayment.rejected, (s, a: any) => { s.loading = false; s.error = a.payload as string; });

		builder
			.addCase(fetchPaymentsByMilestones.pending, (s) => { s.loading = true; s.error = null; })
			.addCase(fetchPaymentsByMilestones.fulfilled, (s, a: PayloadAction<{ data: Payment[]; totalCount?: number }>) => { s.loading = false; s.items = a.payload.data || []; s.totalCount = a.payload.totalCount || 0; })
			.addCase(fetchPaymentsByMilestones.rejected, (s, a: any) => { s.loading = false; s.error = a.payload as string; });
	}
});

export const { clearPaymentsError, clearCurrentPayment } = paymentsSlice.actions;
export default paymentsSlice.reducer;
